import React from 'react';
import {
  Box,
  Button,
  Card as CardInfo,
  CardContent,
  Drawer,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import useSWR from 'swr';
import dayjs from 'dayjs';
import Api from '../../../api';
import {
  accentColors,
  secondaryColors,
  warningColors
} from '../../../theme/themeConfig';

export function StatusPill({ status }: { status: string }) {
  const s = status?.toUpperCase();
  const map: Record<string, { bg: string; color: string }> = {
    CANCELLED: { bg: '#FFEBEE', color: '#C62828' },
    PAID: { bg: '#E8F5E9', color: '#2E7D32' },
    PENDING: { bg: '#FFF8E1', color: '#E65100' }
  };
  const c = map[s] ?? { bg: '#F5F6F8', color: '#4B4D56' };
  return (
    <Box
      component="span"
      sx={{
        backgroundColor: c.bg,
        borderRadius: '20px',
        color: c.color,
        display: 'inline-block',
        fontSize: '11px',
        fontWeight: 600,
        px: '10px',
        py: '3px'
      }}
    >
      {status
        ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        : ''}
    </Box>
  );
}

interface Props {
  invoiceId: string | null;
  onClose: () => void;
  onMarkPaid: () => void;
  onResend: () => void;
  open: boolean;
}

const InvoiceDrawer: React.FC<Props> = function InvoiceDrawer({
  invoiceId,
  onClose,
  onMarkPaid,
  onResend,
  open
}) {
  const { data: info } = useSWR(
    open && invoiceId ? invoiceId : null,
    Api.InvoicesApi.getInvoicesById
  );

  const toCurrency = (v: any) =>
    new Intl.NumberFormat('en-US', {
      currency: 'USD',
      style: 'currency'
    }).format(parseFloat((parseFloat(v) || 0).toFixed(2)));

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          boxShadow: '-4px 0 20px rgba(0,0,0,0.08)',
          width: 420
        }
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid #E2E4EA',
          display: 'flex',
          justifyContent: 'space-between',
          px: 2,
          py: '12px'
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
          Invoice Detail
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {!info && (
        <Box
          sx={{ color: '#7D8090', fontSize: '13px', p: 3, textAlign: 'center' }}
        >
          Loading…
        </Box>
      )}

      {info && (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {/* Seen banner */}
          <Box
            sx={{
              alignItems: 'center',
              borderBottom: '1px solid #E2E4EA',
              display: 'flex',
              gap: 1,
              px: 2,
              py: '8px'
            }}
          >
            <VisibilityOutlinedIcon
              sx={{
                color: info.lastSeen
                  ? accentColors.ACCENT_3
                  : warningColors.ERROR,
                fontSize: 15
              }}
            />
            <Typography
              sx={{
                color: info.lastSeen
                  ? accentColors.ACCENT_3
                  : warningColors.ERROR,
                fontSize: '12px',
                fontWeight: 500
              }}
            >
              {info.lastSeen
                ? 'Seen by the client'
                : 'Pending to be seen by the client'}
            </Typography>
          </Box>

          {/* Invoice # + status */}
          <Box sx={{ borderBottom: '1px solid #E2E4EA', px: 2, py: '14px' }}>
            <Box
              sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: '8px' }}
            >
              <Typography
                sx={{ color: secondaryColors.SECONDARY, fontSize: '12px' }}
              >
                Invoice:
              </Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
                #{info.invoiceNumber}
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <StatusPill status={info.status} />
              </Box>
            </Box>
            <Typography
              sx={{ color: '#0f1c2e', fontSize: '13px', fontWeight: 600 }}
            >
              {info.clientName}
            </Typography>
            <Typography
              sx={{
                color: secondaryColors.SECONDARY,
                fontSize: '12px',
                mt: '2px'
              }}
            >
              {info.companyName}
            </Typography>
            {info.projectName && (
              <Typography
                sx={{
                  color: secondaryColors.SECONDARY,
                  fontSize: '12px',
                  mt: '2px'
                }}
              >
                {info.projectName}
              </Typography>
            )}
          </Box>

          {/* Dates */}
          <Box sx={{ borderBottom: '1px solid #E2E4EA', px: 2, py: '12px' }}>
            {[
              {
                label: 'Date',
                value: dayjs(info.sendDate).format('MMM DD, YYYY')
              },
              {
                label: 'Due Date',
                value: dayjs(info.dueDate).format('MMM DD, YYYY')
              },
              ...(info.rangeStartDate && info.rangeEndDate
                ? [
                    {
                      label: 'Service Range',
                      value: `${dayjs(info.rangeStartDate).format(
                        'MMM DD'
                      )} – ${dayjs(info.rangeEndDate).format('MMM DD, YYYY')}`
                    }
                  ]
                : []),
              {
                label: 'Payment Term',
                value: `Due in ${dayjs(info.dueDate).diff(
                  dayjs(info.sendDate),
                  'day'
                )} days`
              }
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: '6px'
                }}
              >
                <Typography
                  sx={{ color: secondaryColors.SECONDARY, fontSize: '12px' }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{ color: '#0f1c2e', fontSize: '12px', fontWeight: 600 }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Services (regular invoices) or SR line items */}
          {(info.services as any)?.length > 0 && (
            <Box sx={{ borderBottom: '1px solid #E2E4EA', px: 2, py: '12px' }}>
              <Typography
                sx={{
                  color: secondaryColors.SECONDARY,
                  fontSize: '12px',
                  mb: '8px'
                }}
              >
                Services
              </Typography>
              <CardInfo
                sx={{ border: '1px solid #E2E4EA', borderRadius: '6px' }}
                variant="outlined"
              >
                {(info.services as any)?.map((service: any) => (
                  <CardContent
                    key={service.id}
                    sx={{ p: '10px 12px !important' }}
                  >
                    <Typography
                      sx={{
                        color: '#0f1c2e',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                    >
                      {service.name}
                    </Typography>
                    {service.tasks?.map((task: any) => (
                      <Typography
                        component="li"
                        key={task.id}
                        sx={{
                          color: secondaryColors.SECONDARY,
                          fontSize: '12px',
                          mt: '2px'
                        }}
                      >
                        {task.name}
                      </Typography>
                    ))}
                  </CardContent>
                ))}
              </CardInfo>
            </Box>
          )}

          {/* Totals */}
          <Box sx={{ borderBottom: '1px solid #E2E4EA', px: 2, py: '12px' }}>
            {[
              {
                bold: false,
                label: 'Subtotal',
                value: toCurrency(info.subTotal)
              },
              {
                bold: false,
                label: `Tax @ ${
                  info.taxes && info.subTotal
                    ? Math.round(
                        (Number(info.taxes) / Number(info.subTotal)) * 100
                      )
                    : 0
                }%`,
                value: toCurrency(info.taxes)
              },
              { bold: true, label: 'Total', value: toCurrency(info.total) }
            ].map(({ label, value, bold }) => (
              <Box
                key={label}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: '4px'
                }}
              >
                <Typography
                  sx={{
                    color: '#0f1c2e',
                    fontSize: bold ? '14px' : '12px',
                    fontWeight: bold ? 700 : 400
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{
                    color: '#0f1c2e',
                    fontSize: bold ? '14px' : '12px',
                    fontWeight: bold ? 700 : 500
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Actions */}
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              px: 2,
              py: '12px'
            }}
          >
            <Typography sx={{ color: '#7D8090', fontSize: '12px' }}>
              {info.lastSeen
                ? 'This invoice has already been seen'
                : 'Awaiting client review'}
            </Typography>
            <Button
              endIcon={<SendIcon sx={{ fontSize: 14 }} />}
              onClick={onResend}
              sx={{
                color: '#005DA2',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none'
              }}
              variant="text"
            >
              Resend Invoice
            </Button>
          </Box>

          {info.status?.toUpperCase() === 'PENDING' && (
            <Box sx={{ pb: 2, px: 2 }}>
              <Button
                fullWidth
                onClick={onMarkPaid}
                sx={{
                  '&:hover': { backgroundColor: '#C8E6C9' },
                  backgroundColor: '#E8F5E9',
                  borderRadius: '8px',
                  color: '#2E7D32',
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'none'
                }}
                variant="text"
              >
                Mark as Paid
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default InvoiceDrawer;
